import { get } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

import { ResponseCodes } from "../types";

enum UrgentShiftNotificationFrequency {
  HIGH = "HIGH",
  MEDIUM = "MEDIUM",
  LOW = "LOW",
}

const UrgentShiftNotificationFrequencySchema = z.nativeEnum(UrgentShiftNotificationFrequency);

const UrgentShiftPreferenceSchema = z.object({
  isOn: z.boolean(),
  notificationFrequency: UrgentShiftNotificationFrequencySchema,
});

const NotificationPreferenceSchema = z.object({
  off: z.boolean(),
  push: z.boolean(),
  email: z.boolean(),
  sms: z.boolean(),
  marketingPush: z.boolean(),
  priorityAccessPush: z.boolean().optional(),
  marketingEmail: z.boolean(),
  marketingSMS: z.boolean(),
  long_code_sms: z.boolean(),
  urgentShiftNotification: UrgentShiftPreferenceSchema.optional(),
  placementsMarketingPush: z.boolean().optional(),
  aTeamPushNotification: z.boolean().optional(),
});

const ResponseSchema = z.object({
  code: z.nativeEnum(ResponseCodes),
  message: z.string().nullable().optional(),
  data: z.string().or(z.undefined()).nullable(),
});

export const getNotificationPreferencesResponseSchema = z
  .object({
    code: z.nativeEnum(ResponseCodes),
    message: z.string().nullable().optional(),
    data: z.object({
      notificationPreferences: NotificationPreferenceSchema.extend({
        last_minute_notifications: NotificationPreferenceSchema.shape.urgentShiftNotification,
      }),
    }),
  })
  .or(ResponseSchema);

type GetNotificationPreferencesResponse = z.infer<typeof getNotificationPreferencesResponseSchema>;

export function useGetNotificationPreferences(): UseQueryResult<GetNotificationPreferencesResponse> {
  return useQuery<GetNotificationPreferencesResponse>({
    queryFn: async () => {
      const response = await get({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/notificationPreferences/fetch`,
        responseSchema: getNotificationPreferencesResponseSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_NOTIFICATION_PREFERENCES_FAILURE,
    },
  });
}
